/**
 * What kind of image this actually is, from the first few bytes.
 *
 * `fetchBinary` hands back untyped bytes on purpose. A retail CDN's
 * Content-Type is frequently `application/octet-stream`, occasionally a lie,
 * and the magic number never is — and the stored Blob's type is what an
 * `<img>` later renders from.
 */

const SIGNATURES: readonly { mime: string; bytes: readonly number[] }[] = [
  { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { mime: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
  { mime: 'image/bmp', bytes: [0x42, 0x4d] },
];

/** Bytes needed to recognise the longest signature, plus the WebP/AVIF boxes. */
const PREFIX = 16;

export async function sniffImageMime(blob: Blob): Promise<string | undefined> {
  const head = new Uint8Array(await blob.slice(0, PREFIX).arrayBuffer());
  return mimeOf(head);
}

/** Split out from the Blob read so it can be tested against a byte array. */
export function mimeOf(head: Uint8Array): string | undefined {
  for (const { mime, bytes } of SIGNATURES) {
    if (bytes.every((byte, index) => head[index] === byte)) return mime;
  }

  // RIFF....WEBP — the format marker sits after the four-byte length.
  if (matches(head, 0, 'RIFF') && matches(head, 8, 'WEBP')) return 'image/webp';

  // ISO base media: a `ftyp` box whose brand names the flavour. AVIF and HEIC
  // both land here, and Safari-era retail CDNs do serve them.
  if (matches(head, 4, 'ftyp')) {
    if (matches(head, 8, 'avif') || matches(head, 8, 'avis')) return 'image/avif';
    if (matches(head, 8, 'heic') || matches(head, 8, 'heix')) return 'image/heic';
  }

  return undefined;
}

function matches(head: Uint8Array, offset: number, ascii: string): boolean {
  return [...ascii].every((character, index) => head[offset + index] === character.charCodeAt(0));
}
